import React, { useState } from 'react';
import { FiPlus, FiTrash2, FiCopy, FiUpload, FiPlay, FiSquare } from 'react-icons/fi';
import { adminAPI, API_ORIGIN } from '../../../../services/api';
import ttsService from '../../../../utils/textToSpeech';

const ExercisesBlockEditor = ({ block, onUpdate, availableVoices = [], setError = () => {}, setSuccess = () => {} }) => {
    const [exercisesData, setExercisesData] = useState({
        exercises: block.exercises || []
    });
    const [playingPhoneticKey, setPlayingPhoneticKey] = useState(null);

    const handleChange = (updated) => {
        setExercisesData(updated);
        onUpdate(updated);
    };

    const addExercise = () => {
        const updated = {
            ...exercisesData,
            exercises: [...exercisesData.exercises, {
                title: '',
                type: 'multiple_choice',
                instructions: '',
                questions: []
            }]
        };
        handleChange(updated);
    };

    const removeExercise = (index) => {
        const updated = {
            ...exercisesData,
            exercises: exercisesData.exercises.filter((_, i) => i !== index)
        };
        handleChange(updated);
    };

    const updateExercise = (index, field, value) => {
        const updated = {
            ...exercisesData,
            exercises: exercisesData.exercises.map((ex, i) => 
                i === index ? { ...ex, [field]: value } : ex
            )
        };
        handleChange(updated);
    };

    const addQuestion = (exerciseIndex) => {
        const exercise = exercisesData.exercises[exerciseIndex];
        const promptDefaults = {
            promptAudioType: 'none',
            promptAudioUrl: '',
            promptTtsText: '',
            promptVoiceId: 'en-US-JennyNeural'
        };
        const newQuestion = {
            questionText: '',
            questionData: { ...promptDefaults },
            correctAnswer: '',
            explanation: ''
        };

        // Инициализация questionData в зависимости от типа упражнения
        switch (exercise.type) {
            case 'multiple_choice':
                newQuestion.questionData = { ...promptDefaults, options: ['', '', '', ''] };
                newQuestion.correctAnswer = 0;
                break;
            case 'true_false':
                newQuestion.questionData = { ...promptDefaults };
                newQuestion.correctAnswer = 'true';
                break;
            case 'fill_gaps':
                newQuestion.questionData = { ...promptDefaults, sentence: '', gaps: [] };
                newQuestion.correctAnswer = [];
                break;
            case 'match':
                newQuestion.questionData = { ...promptDefaults, pairs: [{ left: '', right: '' }] };
                newQuestion.correctAnswer = {};
                break;
            case 'arrange':
                newQuestion.questionData = { ...promptDefaults, words: [] };
                newQuestion.correctAnswer = [];
                break;
            case 'text_answer':
                newQuestion.questionData = { ...promptDefaults };
                newQuestion.correctAnswer = '';
                break;
            case 'phonetic_dictation':
                newQuestion.questionText = 'Прослушайте аудио и напишите услышанное предложение.';
                newQuestion.questionData = {
                    audioUrl: '',
                    voiceId: 'en-US-JennyNeural',
                    audioType: 'auto'
                };
                newQuestion.correctAnswer = '';
                break;
            default:
                break;
        }

        updateExercise(exerciseIndex, 'questions', [...exercise.questions, newQuestion]);
    };

    const removeQuestion = (exerciseIndex, questionIndex) => {
        const exercise = exercisesData.exercises[exerciseIndex];
        updateExercise(
            exerciseIndex,
            'questions',
            exercise.questions.filter((_, i) => i !== questionIndex)
        );
    };

    const updateQuestion = (exerciseIndex, questionIndex, field, value) => {
        const exercise = exercisesData.exercises[exerciseIndex];
        const updatedQuestions = exercise.questions.map((q, i) =>
            i === questionIndex ? { ...q, [field]: value } : q
        );
        updateExercise(exerciseIndex, 'questions', updatedQuestions);
    };

    const renderQuestionEditor = (exercise, exerciseIndex, question, questionIndex) => {
        const renderQuestionPromptAudioControls = () => {
            const qd = question.questionData || {};
            const promptAudioType = qd.promptAudioType || 'none';
            const key = `prompt-${exerciseIndex}-${questionIndex}`;

            const playPrompt = () => {
                if (promptAudioType === 'custom' && qd.promptAudioUrl) {
                    const a = new Audio(`${API_ORIGIN}${qd.promptAudioUrl}`);
                    setPlayingPhoneticKey(key);
                    a.onended = () => setPlayingPhoneticKey(null);
                    a.onerror = () => {
                        setPlayingPhoneticKey(null);
                        setError('Не удалось воспроизвести аудио инструкции');
                    };
                    a.play().catch(() => setPlayingPhoneticKey(null));
                    return;
                }
                if (promptAudioType === 'tts') {
                    const promptText = (qd.promptTtsText || '').trim();
                    if (!promptText) {
                        setError('Введите текст для авто-озвучки инструкции');
                        return;
                    }
                    setPlayingPhoneticKey(key);
                    ttsService.speak(promptText, {
                        rate: 1,
                        lang: 'en-US',
                        voice: qd.promptVoiceId || 'en-US-JennyNeural'
                    }).then(() => setPlayingPhoneticKey(null)).catch(() => {
                        setPlayingPhoneticKey(null);
                        setError('Ошибка озвучки инструкции');
                    });
                }
            };

            return (
                <div style={{ marginTop: '12px', padding: '12px', border: '1px solid #e8e8e8', borderRadius: '8px', background: '#fafafa' }}>
                    <label className="form-label" style={{ fontSize: '12px' }}>Аудио для задания (для ученика)</label>
                    <div style={{ display: 'flex', gap: '14px', marginBottom: '10px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <input type="radio" checked={promptAudioType === 'none'} onChange={() => updateQuestion(exerciseIndex, questionIndex, 'questionData', { ...qd, promptAudioType: 'none' })} />
                            Без аудио
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <input type="radio" checked={promptAudioType === 'custom'} onChange={() => updateQuestion(exerciseIndex, questionIndex, 'questionData', { ...qd, promptAudioType: 'custom' })} />
                            Загруженный файл
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <input type="radio" checked={promptAudioType === 'tts'} onChange={() => updateQuestion(exerciseIndex, questionIndex, 'questionData', { ...qd, promptAudioType: 'tts' })} />
                            Авто-озвучка (TTS)
                        </label>
                    </div>

                    {promptAudioType === 'custom' && (
                        <div style={{ marginBottom: '10px' }}>
                            <input
                                type="file"
                                accept="audio/*"
                                style={{ display: 'none' }}
                                id={`prompt-audio-${block.id || 'b'}-${exerciseIndex}-${questionIndex}`}
                                onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (!file.type.startsWith('audio/')) {
                                        setError('Выберите аудио файл');
                                        return;
                                    }
                                    try {
                                        const formData = new FormData();
                                        formData.append('audio', file);
                                        const response = await adminAPI.uploadSentenceAudio(formData);
                                        updateQuestion(exerciseIndex, questionIndex, 'questionData', {
                                            ...qd,
                                            promptAudioUrl: response.data.audioUrl,
                                            promptAudioType: 'custom'
                                        });
                                        setSuccess('Аудио инструкции загружено');
                                    } catch (err) {
                                        console.error(err);
                                        setError('Ошибка загрузки аудио инструкции');
                                    }
                                }}
                            />
                            <label htmlFor={`prompt-audio-${block.id || 'b'}-${exerciseIndex}-${questionIndex}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px', background: qd.promptAudioUrl ? '#52c41a' : '#4a90e2', color: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                                <FiUpload /> {qd.promptAudioUrl ? 'Файл загружен' : 'Загрузить аудио'}
                            </label>
                        </div>
                    )}

                    {promptAudioType === 'tts' && (
                        <div style={{ marginBottom: '10px' }}>
                            <textarea className="form-input" rows={2} value={qd.promptTtsText || ''} onChange={(e) => updateQuestion(exerciseIndex, questionIndex, 'questionData', { ...qd, promptTtsText: e.target.value })} placeholder="Введите текст инструкции для озвучки" />
                            <select className="form-input" value={qd.promptVoiceId || 'en-US-JennyNeural'} onChange={(e) => updateQuestion(exerciseIndex, questionIndex, 'questionData', { ...qd, promptVoiceId: e.target.value })}>
                                {(availableVoices.length ? availableVoices : [{ ShortName: 'en-US-JennyNeural', DisplayName: 'Jenny (US)' }]).map((v) => (
                                    <option key={v.ShortName} value={v.ShortName}>{v.DisplayName || v.ShortName}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {promptAudioType !== 'none' && (
                        <button type="button" onClick={playPrompt} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#4a90e2', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                            <FiPlay size={14} /> Прослушать
                        </button>
                    )}
                </div>
            );
        };

        switch (exercise.type) {
            case 'multiple_choice':
                return (
                    <div>
                        <label className="form-label" style={{ fontSize: '12px' }}>Текст вопроса</label>
                        <input
                            type="text"
                            className="form-input"
                            value={question.questionText}
                            onChange={(e) => updateQuestion(exerciseIndex, questionIndex, 'questionText', e.target.value)}
                            placeholder="Например: What is the capital of France?"
                        />

                        <label className="form-label" style={{ fontSize: '12px', marginTop: '10px' }}>Варианты ответов</label>
                        {(question.questionData?.options || []).map((option, optIndex) => (
                            <div key={optIndex} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                                <input
                                    type="radio"
                                    name={`correct-${exerciseIndex}-${questionIndex}`}
                                    checked={question.correctAnswer === optIndex}
                                    onChange={() => updateQuestion(exerciseIndex, questionIndex, 'correctAnswer', optIndex)}
                                />
                                <input
                                    type="text"
                                    className="form-input"
                                    style={{ flex: 1 }}
                                    value={option}
                                    onChange={(e) => {
                                        const newOptions = [...question.questionData.options];
                                        newOptions[optIndex] = e.target.value;
                                        updateQuestion(exerciseIndex, questionIndex, 'questionData', {
                                            ...question.questionData,
                                            options: newOptions
                                        });
                                    }}
                                    placeholder={`Вариант ${optIndex + 1}`}
                                />
                            </div>
                        ))}
                    </div>
                );

            case 'true_false':
                return (
                    <div>
                        <label className="form-label" style={{ fontSize: '12px' }}>Утверждение</label>
                        <textarea
                            className="form-input"
                            value={question.questionText}
                            onChange={(e) => updateQuestion(exerciseIndex, questionIndex, 'questionText', e.target.value)}
                            rows={2}
                            placeholder="Например: The Earth is flat."
                        />

                        <label className="form-label" style={{ fontSize: '12px', marginTop: '10px' }}>Правильный ответ</label>
                        <div style={{ display: 'flex', gap: '15px', marginTop: '8px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name={`true-false-${exerciseIndex}-${questionIndex}`}
                                    checked={question.correctAnswer === true || question.correctAnswer === 'true'}
                                    onChange={() => updateQuestion(exerciseIndex, questionIndex, 'correctAnswer', 'true')}
                                />
                                <span style={{ fontSize: '14px' }}>Правда (True)</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name={`true-false-${exerciseIndex}-${questionIndex}`}
                                    checked={question.correctAnswer === false || question.correctAnswer === 'false'}
                                    onChange={() => updateQuestion(exerciseIndex, questionIndex, 'correctAnswer', 'false')}
                                />
                                <span style={{ fontSize: '14px' }}>Ложь (False)</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name={`true-false-${exerciseIndex}-${questionIndex}`}
                                    checked={question.correctAnswer === 'not_stated'}
                                    onChange={() => updateQuestion(exerciseIndex, questionIndex, 'correctAnswer', 'not_stated')}
                                />
                                <span style={{ fontSize: '14px' }}>Не указано (Not stated)</span>
                            </label>
                        </div>
                    </div>
                );

            case 'fill_gaps':
                const gapsData = question.questionData || { sentence: '', gaps: [] };
                const correctAnswers = Array.isArray(question.correctAnswer) ? question.correctAnswer : [];
                
                return (
                    <div>
                        <label className="form-label" style={{ fontSize: '12px' }}>
                            Предложение с пропусками
                        </label>
                        <p style={{ fontSize: '11px', color: '#999', marginBottom: '6px' }}>
                            Используйте ___ для обозначения пропуска. Например: "I ___ to school every day."
                        </p>
                        <textarea
                            className="form-input"
                            value={gapsData.sentence || ''}
                            onChange={(e) => {
                                const sentence = e.target.value;
                                const gaps = (sentence.match(/___/g) || []).length;
                                
                                // Обновляем questionData с новым предложением
                                const newQuestionData = {
                                    ...(question.questionData || {}),
                                    sentence,
                                    gaps: Array(gaps).fill('')
                                };
                                
                                // Обновляем и questionData, и correctAnswer за один раз
                                const exercise = exercisesData.exercises[exerciseIndex];
                                const updatedQuestions = exercise.questions.map((q, i) =>
                                    i === questionIndex ? {
                                        ...q,
                                        questionData: newQuestionData,
                                        correctAnswer: Array(gaps).fill('')
                                    } : q
                                );
                                updateExercise(exerciseIndex, 'questions', updatedQuestions);
                            }}
                            rows={2}
                            placeholder="I ___ to school every day."
                        />

                        <label className="form-label" style={{ fontSize: '12px', marginTop: '10px' }}>
                            Правильные ответы для пропусков
                        </label>
                        {correctAnswers.length > 0 ? (
                            correctAnswers.map((answer, gapIndex) => (
                                <input
                                    key={gapIndex}
                                    type="text"
                                    className="form-input"
                                    style={{ marginBottom: '6px' }}
                                    value={answer || ''}
                                    onChange={(e) => {
                                        const newAnswers = [...correctAnswers];
                                        newAnswers[gapIndex] = e.target.value;
                                        updateQuestion(exerciseIndex, questionIndex, 'correctAnswer', newAnswers);
                                    }}
                                    placeholder={`Правильное слово для пропуска ${gapIndex + 1}`}
                                />
                            ))
                        ) : (
                            <p style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                                Сначала добавьте предложение с пропусками (___) выше
                            </p>
                        )}
                    </div>
                );

            case 'match':
                return (
                    <div>
                        <label className="form-label" style={{ fontSize: '12px' }}>Инструкция</label>
                        <p style={{ fontSize: '11px', color: '#999', marginBottom: '6px' }}>
                            Студент должен соединить слова/фразы слева с соответствующими справа
                        </p>
                        <input
                            type="text"
                            className="form-input"
                            value={question.questionText}
                            onChange={(e) => updateQuestion(exerciseIndex, questionIndex, 'questionText', e.target.value)}
                            placeholder="Соедините слова с их переводом"
                        />

                        <label className="form-label" style={{ fontSize: '12px', marginTop: '10px' }}>Пары для сопоставления</label>
                        {(question.questionData?.pairs || []).map((pair, pairIndex) => (
                            <div key={pairIndex} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <input
                                    type="text"
                                    className="form-input"
                                    style={{ flex: 1 }}
                                    value={pair.left}
                                    onChange={(e) => {
                                        const newPairs = [...question.questionData.pairs];
                                        newPairs[pairIndex].left = e.target.value;
                                        updateQuestion(exerciseIndex, questionIndex, 'questionData', {
                                            ...question.questionData,
                                            pairs: newPairs
                                        });
                                    }}
                                    placeholder="Слева (например, apple)"
                                />
                                <span style={{ lineHeight: '40px' }}>↔</span>
                                <input
                                    type="text"
                                    className="form-input"
                                    style={{ flex: 1 }}
                                    value={pair.right}
                                    onChange={(e) => {
                                        const newPairs = [...question.questionData.pairs];
                                        newPairs[pairIndex].right = e.target.value;
                                        updateQuestion(exerciseIndex, questionIndex, 'questionData', {
                                            ...question.questionData,
                                            pairs: newPairs
                                        });
                                    }}
                                    placeholder="Справа (например, яблоко)"
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newPairs = question.questionData.pairs.filter((_, i) => i !== pairIndex);
                                        updateQuestion(exerciseIndex, questionIndex, 'questionData', {
                                            ...question.questionData,
                                            pairs: newPairs
                                        });
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#ff4d4f',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <FiTrash2 />
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() => {
                                const newPairs = [...(question.questionData?.pairs || []), { left: '', right: '' }];
                                updateQuestion(exerciseIndex, questionIndex, 'questionData', {
                                    ...question.questionData,
                                    pairs: newPairs
                                });
                            }}
                            style={{
                                padding: '6px 12px',
                                background: '#52c41a',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                        >
                            + Добавить пару
                        </button>
                    </div>
                );

            case 'phonetic_dictation': {
                const qd = question.questionData || {};
                const transcript = typeof question.correctAnswer === 'string' ? question.correctAnswer : '';
                const audioType = qd.audioType || (qd.audioUrl ? 'custom' : 'auto');
                const key = `${exerciseIndex}-${questionIndex}`;

                const playPhonetic = () => {
                    if (audioType === 'custom' && qd.audioUrl) {
                        const a = new Audio(`${API_ORIGIN}${qd.audioUrl}`);
                        setPlayingPhoneticKey(key);
                        a.onended = () => setPlayingPhoneticKey(null);
                        a.onerror = () => {
                            setPlayingPhoneticKey(null);
                            setError('Не удалось воспроизвести аудио');
                        };
                        a.play().catch(() => setPlayingPhoneticKey(null));
                        return;
                    }
                    if (!transcript.trim()) {
                        setError('Введите эталонный текст для автоматической озвучки');
                        return;
                    }
                    setPlayingPhoneticKey(key);
                    ttsService.speak(transcript.trim(), {
                        rate: 1,
                        lang: 'en-US',
                        voice: qd.voiceId || 'en-US-JennyNeural'
                    }).then(() => setPlayingPhoneticKey(null)).catch(() => {
                        setPlayingPhoneticKey(null);
                        setError('Ошибка озвучки');
                    });
                };

                const stopPhonetic = () => {
                    ttsService.cancel();
                    setPlayingPhoneticKey(null);
                };

                return (
                    <div>
                        <label className="form-label" style={{ fontSize: '12px' }}>
                            Подсказка для ученика (не показывает эталонный текст)
                        </label>
                        <input
                            type="text"
                            className="form-input"
                            value={question.questionText || ''}
                            onChange={(e) => updateQuestion(exerciseIndex, questionIndex, 'questionText', e.target.value)}
                            placeholder="Например: Прослушайте и запишите услышанное предложение."
                        />

                        <label className="form-label" style={{ fontSize: '12px', marginTop: '10px' }}>
                            Эталонный текст (EN) — по нему проверяется ответ; ученик его не видит
                        </label>
                        <textarea
                            className="form-input"
                            rows={3}
                            value={transcript}
                            onChange={(e) => updateQuestion(exerciseIndex, questionIndex, 'correctAnswer', e.target.value)}
                            placeholder="Например: She usually walks to work in the morning."
                        />

                        <label className="form-label" style={{ fontSize: '12px', marginTop: '10px' }}>Источник аудио</label>
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '10px', flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    checked={audioType === 'auto'}
                                    onChange={() => updateQuestion(exerciseIndex, questionIndex, 'questionData', { ...qd, audioType: 'auto' })}
                                />
                                Авто-озвучка (TTS)
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    checked={audioType === 'custom'}
                                    onChange={() => updateQuestion(exerciseIndex, questionIndex, 'questionData', { ...qd, audioType: 'custom' })}
                                />
                                Загруженный файл
                            </label>
                        </div>

                        {audioType === 'auto' && (
                            <div style={{ marginBottom: '10px' }}>
                                <label className="form-label" style={{ fontSize: '12px' }}>Голос</label>
                                <select
                                    className="form-input"
                                    value={qd.voiceId || 'en-US-JennyNeural'}
                                    onChange={(e) => updateQuestion(exerciseIndex, questionIndex, 'questionData', { ...qd, voiceId: e.target.value })}
                                >
                                    {(availableVoices.length ? availableVoices : [{ ShortName: 'en-US-JennyNeural', DisplayName: 'Jenny (US)' }]).map((v) => (
                                        <option key={v.ShortName} value={v.ShortName}>{v.DisplayName || v.ShortName}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {audioType === 'custom' && (
                            <div style={{ marginBottom: '10px' }}>
                                <input
                                    type="file"
                                    accept="audio/*"
                                    style={{ display: 'none' }}
                                    id={`phonetic-audio-${block.id || 'b'}-${exerciseIndex}-${questionIndex}`}
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        if (!file.type.startsWith('audio/')) {
                                            setError('Выберите аудио файл');
                                            return;
                                        }
                                        try {
                                            const formData = new FormData();
                                            formData.append('audio', file);
                                            const response = await adminAPI.uploadSentenceAudio(formData);
                                            updateQuestion(exerciseIndex, questionIndex, 'questionData', {
                                                ...qd,
                                                audioUrl: response.data.audioUrl,
                                                audioType: 'custom'
                                            });
                                            setSuccess('Аудио загружено');
                                        } catch (err) {
                                            console.error(err);
                                            setError('Ошибка загрузки аудио');
                                        }
                                    }}
                                />
                                <label
                                    htmlFor={`phonetic-audio-${block.id || 'b'}-${exerciseIndex}-${questionIndex}`}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '8px 14px',
                                        background: qd.audioUrl ? '#52c41a' : '#4a90e2',
                                        color: 'white',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px'
                                    }}
                                >
                                    <FiUpload /> {qd.audioUrl ? 'Файл загружен' : 'Загрузить аудио'}
                                </label>
                                {qd.audioUrl && (
                                    <button
                                        type="button"
                                        onClick={() => updateQuestion(exerciseIndex, questionIndex, 'questionData', { ...qd, audioUrl: '' })}
                                        style={{ marginLeft: '10px', fontSize: '12px', color: '#ff4d4f', background: 'none', border: 'none', cursor: 'pointer' }}
                                    >
                                        Удалить файл
                                    </button>
                                )}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                            {playingPhoneticKey === key ? (
                                <button type="button" onClick={stopPhonetic} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#ff4d4f', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                                    <FiSquare size={14} /> Стоп
                                </button>
                            ) : (
                                <button type="button" onClick={playPhonetic} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#4a90e2', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                                    <FiPlay size={14} /> Прослушать (как у ученика)
                                </button>
                            )}
                        </div>
                        {audioType === 'custom' && qd.audioUrl && (
                            <audio controls src={`${API_ORIGIN}${qd.audioUrl}`} style={{ width: '100%', marginTop: '10px' }} />
                        )}
                    </div>
                );
            }

            case 'arrange':
                // Используем текстовое представление для редактирования
                const arrangeSentence = question.questionData?.sentence || question.correctAnswer?.join(' ') || '';
                
                return (
                    <div>
                        <label className="form-label" style={{ fontSize: '12px' }}>Инструкция</label>
                        <input
                            type="text"
                            className="form-input"
                            value={question.questionText || ''}
                            onChange={(e) => updateQuestion(exerciseIndex, questionIndex, 'questionText', e.target.value)}
                            placeholder="Расставьте слова в правильном порядке"
                        />

                        <label className="form-label" style={{ fontSize: '12px', marginTop: '10px' }}>
                            Правильное предложение
                        </label>
                        <p style={{ fontSize: '11px', color: '#999', marginBottom: '6px' }}>
                            Введите предложение в правильном порядке. Оно будет автоматически разбито на слова и перемешано для студента.
                        </p>
                        <input
                            type="text"
                            className="form-input"
                            value={arrangeSentence}
                            onChange={(e) => {
                                const text = e.target.value;
                                const words = text.trim() ? text.trim().split(/\s+/) : [];
                                
                                // Обновляем все за один раз, сохраняя и текст, и слова
                                const exercise = exercisesData.exercises[exerciseIndex];
                                const updatedQuestions = exercise.questions.map((q, i) =>
                                    i === questionIndex ? {
                                        ...q,
                                        correctAnswer: words,
                                        questionData: { ...(q.questionData || {}), sentence: text, words }
                                    } : q
                                );
                                updateExercise(exerciseIndex, 'questions', updatedQuestions);
                            }}
                            placeholder="I go to school every day"
                        />
                    </div>
                );

            case 'text_answer':
                return (
                    <div>
                        <label className="form-label" style={{ fontSize: '12px' }}>Формулировка задания</label>
                        <textarea
                            className="form-input"
                            rows={3}
                            value={question.questionText || ''}
                            onChange={(e) => updateQuestion(exerciseIndex, questionIndex, 'questionText', e.target.value)}
                            placeholder="Например: Переведите предложение или ответьте письменно на вопрос…"
                        />
                        <label className="form-label" style={{ fontSize: '12px', marginTop: '10px' }}>
                            Образец ответа (только для вас при проверке; ученик не видит)
                        </label>
                        <textarea
                            className="form-input"
                            rows={2}
                            value={typeof question.correctAnswer === 'string' ? question.correctAnswer : ''}
                            onChange={(e) => updateQuestion(exerciseIndex, questionIndex, 'correctAnswer', e.target.value)}
                            placeholder="Необязательно — подсказка при ручной проверке"
                        />
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div style={{ display: 'grid', gap: '20px' }}>
            <div className="exercise-block-header">
                <h3 style={{ margin: 0 }}>Упражнения ({exercisesData.exercises.length})</h3>
            </div>
            <button
                type="button"
                onClick={addExercise}
                className="add-exercise-btn"
                style={{
                    padding: '10px 16px',
                    background: '#52c41a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: '500'
                }}
            >
                <FiPlus /> Добавить упражнение
            </button>

            {exercisesData.exercises.length === 0 && (
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    background: '#f9f9f9',
                    borderRadius: '8px',
                    color: '#999'
                }}>
                    Нет упражнений. Нажмите "Добавить упражнение" чтобы создать первое.
                </div>
            )}

            {exercisesData.exercises.map((exercise, exIndex) => (
                <div
                    key={exIndex}
                    style={{
                        padding: '20px',
                        background: '#f5f5f5',
                        borderRadius: '8px',
                        border: '2px solid #e0e0e0'
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                        <h4 style={{ margin: 0 }}>Упражнение {exIndex + 1}</h4>
                        <button
                            type="button"
                            onClick={() => removeExercise(exIndex)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#ff4d4f',
                                cursor: 'pointer',
                                fontSize: '18px'
                            }}
                        >
                            <FiTrash2 />
                        </button>
                    </div>

                    <div style={{ display: 'grid', gap: '15px' }}>
                        <div>
                            <label className="form-label">Название упражнения</label>
                            <input
                                type="text"
                                className="form-input"
                                value={exercise.title}
                                onChange={(e) => updateExercise(exIndex, 'title', e.target.value)}
                                placeholder="Например: Выберите правильный ответ"
                            />
                        </div>

                        <div>
                            <label className="form-label">Тип упражнения</label>
                            <select
                                className="form-input"
                                value={exercise.type}
                                onChange={(e) => {
                                    const newType = e.target.value;
                                    // Обновляем тип
                                    const updatedExercises = [...exercisesData.exercises];
                                    updatedExercises[exIndex] = {
                                        ...updatedExercises[exIndex],
                                        type: newType,
                                        questions: [] // Очищаем вопросы при смене типа
                                    };
                                    handleChange({ ...exercisesData, exercises: updatedExercises });
                                }}
                            >
                                <option value="multiple_choice">Выбор ответа (Multiple Choice)</option>
                                <option value="true_false">Правда/Ложь / Не указано (True/False/Not stated)</option>
                                <option value="fill_gaps">Заполнить пропуски (Fill in the Gaps)</option>
                                <option value="match">Сопоставление (Match)</option>
                                <option value="arrange">Расставить слова (Arrange Words)</option>
                                <option value="text_answer">Текстовый ответ (на проверку преподавателю)</option>
                                <option value="phonetic_dictation">Фонетический диктант</option>
                            </select>
                        </div>

                        <div>
                            <label className="form-label">Инструкции</label>
                            <textarea
                                className="form-input"
                                value={exercise.instructions}
                                onChange={(e) => updateExercise(exIndex, 'instructions', e.target.value)}
                                rows={2}
                                placeholder="Краткое описание что нужно сделать в упражнении"
                            />
                        </div>

                        {exercise.type !== 'phonetic_dictation' && (
                            <div style={{ border: '1px solid #e8e8e8', borderRadius: '8px', padding: '12px', background: '#fafafa' }}>
                                <label className="form-label" style={{ fontSize: '12px' }}>Аудио для всего задания (в начале упражнения)</label>
                                {(() => {
                                    const firstQ = exercise.questions?.[0];
                                    const qd = firstQ?.questionData || {};
                                    const promptAudioType = qd.promptAudioType || 'none';
                                    const updateAllQuestionsPrompt = (patch) => {
                                        const updatedQuestions = (exercise.questions || []).map((q) => ({
                                            ...q,
                                            questionData: { ...(q.questionData || {}), ...patch }
                                        }));
                                        updateExercise(exIndex, 'questions', updatedQuestions);
                                    };

                                    return (
                                        <>
                                            <div style={{ display: 'flex', gap: '14px', marginBottom: '10px', flexWrap: 'wrap' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                                    <input type="radio" checked={promptAudioType === 'none'} onChange={() => updateAllQuestionsPrompt({ promptAudioType: 'none' })} />
                                                    Без аудио
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                                    <input type="radio" checked={promptAudioType === 'custom'} onChange={() => updateAllQuestionsPrompt({ promptAudioType: 'custom' })} />
                                                    Загруженный файл
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                                    <input type="radio" checked={promptAudioType === 'tts'} onChange={() => updateAllQuestionsPrompt({ promptAudioType: 'tts' })} />
                                                    Авто-озвучка (TTS)
                                                </label>
                                            </div>

                                            {promptAudioType === 'custom' && (
                                                <div>
                                                    <input
                                                        type="file"
                                                        accept="audio/*"
                                                        style={{ display: 'none' }}
                                                        id={`exercise-prompt-audio-${block.id || 'b'}-${exIndex}`}
                                                        onChange={async (e) => {
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;
                                                            try {
                                                                const formData = new FormData();
                                                                formData.append('audio', file);
                                                                const response = await adminAPI.uploadSentenceAudio(formData);
                                                                updateAllQuestionsPrompt({ promptAudioType: 'custom', promptAudioUrl: response.data.audioUrl });
                                                                setSuccess('Аудио задания загружено');
                                                            } catch (err) {
                                                                console.error(err);
                                                                setError('Ошибка загрузки аудио');
                                                            }
                                                        }}
                                                    />
                                                    <label htmlFor={`exercise-prompt-audio-${block.id || 'b'}-${exIndex}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px', background: qd.promptAudioUrl ? '#52c41a' : '#4a90e2', color: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                                                        <FiUpload /> {qd.promptAudioUrl ? 'Файл загружен' : 'Загрузить аудио'}
                                                    </label>
                                                </div>
                                            )}

                                            {promptAudioType === 'tts' && (
                                                <div>
                                                    <textarea className="form-input" rows={2} value={qd.promptTtsText || ''} onChange={(e) => updateAllQuestionsPrompt({ promptAudioType: 'tts', promptTtsText: e.target.value })} placeholder="Текст, который услышит ученик в начале задания" />
                                                    <select className="form-input" value={qd.promptVoiceId || 'en-US-JennyNeural'} onChange={(e) => updateAllQuestionsPrompt({ promptAudioType: 'tts', promptVoiceId: e.target.value })}>
                                                        {(availableVoices.length ? availableVoices : [{ ShortName: 'en-US-JennyNeural', DisplayName: 'Jenny (US)' }]).map((v) => (
                                                            <option key={v.ShortName} value={v.ShortName}>{v.DisplayName || v.ShortName}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}

                                            {(!exercise.questions || exercise.questions.length === 0) && (
                                                <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                                                    Добавьте хотя бы один вопрос, чтобы аудио прикрепилось к заданию.
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        )}

                        {/* Вопросы */}
                        <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                <span style={{ fontWeight: '600' }}>Вопросы ({exercise.questions.length})</span>
                                <button
                                    type="button"
                                    onClick={() => addQuestion(exIndex)}
                                    style={{
                                        padding: '6px 12px',
                                        background: '#4a90e2',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                    }}
                                >
                                    + Добавить вопрос
                                </button>
                            </div>

                            {exercise.questions.length === 0 && (
                                <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    background: 'white',
                                    borderRadius: '6px',
                                    color: '#999',
                                    fontSize: '13px'
                                }}>
                                    Нет вопросов. Добавьте первый вопрос для этого упражнения.
                                </div>
                            )}

                            {exercise.questions.map((question, qIndex) => (
                                <div
                                    key={qIndex}
                                    style={{
                                        padding: '15px',
                                        background: 'white',
                                        borderRadius: '6px',
                                        marginBottom: '10px',
                                        border: '1px solid #e0e0e0'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                        <span style={{ fontSize: '13px', fontWeight: '600' }}>
                                            Вопрос {qIndex + 1}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeQuestion(exIndex, qIndex)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#ff4d4f',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <FiTrash2 size={16} />
                                        </button>
                                    </div>

                                    {renderQuestionEditor(exercise, exIndex, question, qIndex)}

                                    <div style={{ marginTop: '12px' }}>
                                        <label className="form-label" style={{ fontSize: '12px' }}>
                                            Объяснение (необязательно)
                                        </label>
                                        <textarea
                                            className="form-input"
                                            value={question.explanation}
                                            onChange={(e) => updateQuestion(exIndex, qIndex, 'explanation', e.target.value)}
                                            rows={2}
                                            placeholder="Объяснение правильного ответа..."
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ExercisesBlockEditor;
